import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

const PageApiQueueContext = createContext(null);

/**
 * Serializes API calls for a page. On unmount (route change), clears the
 * queue and aborts the in-flight request so later calls never start.
 */
export const PageApiQueueProvider = ({ children }) => {
  const queueRef = useRef([]);
  const processingRef = useRef(false);
  const activeControllerRef = useRef(null);
  const activeTokenRef = useRef(null);
  const activeIdRef = useRef(null);
  const isMountedRef = useRef(true);
  const tokenSeqRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      queueRef.current = [];
      if (activeControllerRef.current) {
        activeControllerRef.current.abort();
        activeControllerRef.current = null;
      }
      activeTokenRef.current = null;
      activeIdRef.current = null;
      processingRef.current = false;
    };
  }, []);

  const processQueue = useCallback(async () => {
    if (processingRef.current || !isMountedRef.current) return;
    processingRef.current = true;

    while (isMountedRef.current) {
      queueRef.current.sort((a, b) => a.priority - b.priority);
      const task = queueRef.current.shift();
      if (!task) break;

      const controller = new AbortController();
      activeControllerRef.current = controller;
      activeTokenRef.current = task.token;
      activeIdRef.current = task.id;

      try {
        await task.execute(controller.signal);
      } catch (e) {
        // Ignore abort / network errors; continue if still on page
      } finally {
        if (activeTokenRef.current === task.token) {
          activeControllerRef.current = null;
          activeTokenRef.current = null;
          activeIdRef.current = null;
        }
      }
    }

    processingRef.current = false;

    if (isMountedRef.current && queueRef.current.length > 0) {
      processQueue();
    }
  }, []);

  const enqueue = useCallback(
    (id, priority, execute) => {
      if (!isMountedRef.current) return () => {};

      queueRef.current = queueRef.current.filter((t) => t.id !== id);

      if (activeIdRef.current === id && activeControllerRef.current) {
        activeControllerRef.current.abort();
      }

      const token = `${id}:${++tokenSeqRef.current}`;
      queueRef.current.push({ id, priority, execute, token });
      processQueue();

      return () => {
        queueRef.current = queueRef.current.filter((t) => t.token !== token);
        if (
          activeTokenRef.current === token &&
          activeControllerRef.current
        ) {
          activeControllerRef.current.abort();
        }
      };
    },
    [processQueue]
  );

  return (
    <PageApiQueueContext.Provider value={{ enqueue }}>
      {children}
    </PageApiQueueContext.Provider>
  );
};

/**
 * Inside PageApiQueueProvider: serial queue.
 * Outside (e.g. Reports): run immediately and independently.
 */
export const usePageApiQueue = () => {
  const ctx = useContext(PageApiQueueContext);

  const enqueueStandalone = useCallback((id, priority, execute) => {
    const controller = new AbortController();
    Promise.resolve()
      .then(() => execute(controller.signal))
      .catch(() => {});
    return () => {
      controller.abort();
    };
  }, []);

  if (ctx) return ctx;
  return { enqueue: enqueueStandalone };
};
